import React, { useState } from 'react';
import LegacyPratusTable from '../../../components/DesignSystems/PratusTable/LegacyPratusTable';
import type { ColumnDescription } from 'react-bootstrap-table-next';
import { useFormContext } from 'react-hook-form';
import { Link } from 'react-router';
import { StylishNewButton } from '../../../components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from '../../../components/SharedIcon/SharedIcon';
import TriggerEditDrawer from '../../DisasterGPTAlerts/TriggerEditDrawer';
import { DGPTAlertTrigger, DGPTAlertCategory } from '../../../types/dgptAlerts';
import { useAlertCategories } from '../hooks/useAlertCategories';
import { useUpdateAlertConfig } from '../hooks/useAlertConfigs';
import { toast } from 'react-toastify';

const TriggersStep: React.FC = () => {
  const form = useFormContext();
  const { setValue, watch, getValues } = form;
  const { data: categories = [] } = useAlertCategories();
  const updateConfig = useUpdateAlertConfig();

  const [showDrawer, setShowDrawer] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<DGPTAlertTrigger | null>(
    null
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const triggers = watch('triggers') || [];

  const handleAddTrigger = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    setEditingTrigger(null);
    setEditingIndex(null);
    setShowDrawer(true);
  };

  const handleEditTrigger = (trigger: DGPTAlertTrigger, index: number) => {
    setEditingTrigger(trigger);
    setEditingIndex(index);
    setShowDrawer(true);
  };

  const handleDeleteTrigger = (index: number) => {
    const updatedTriggers = [...triggers];
    updatedTriggers.splice(index, 1);
    setValue('triggers', updatedTriggers);
    saveTriggersToConfig(updatedTriggers);
  };

  const handleSaveTrigger = (trigger: DGPTAlertTrigger) => {
    const updatedTriggers = [...triggers];

    if (editingIndex !== null) {
      // Update existing trigger
      updatedTriggers[editingIndex] = trigger;
    } else {
      // Add new trigger
      updatedTriggers.push(trigger);
    }

    setValue('triggers', updatedTriggers);
    saveTriggersToConfig(updatedTriggers);
    setShowDrawer(false);
    setEditingTrigger(null);
    setEditingIndex(null);
  };

  const saveTriggersToConfig = async (updatedTriggers: DGPTAlertTrigger[]) => {
    try {
      const currentConfig = getValues();

      // Only save to backend if the configuration already exists (has an ID)
      if (!currentConfig.id) {
        // Configuration doesn't exist yet, just update the form state
        return;
      }

      // Clean up the triggers to ensure consistent structure
      const cleanedTriggers = updatedTriggers.map((trigger) => ({
        name: trigger.name,
        categoryId: trigger.categoryId,
        data_sources: trigger.data_sources.map((ds) => ({
          source: ds.source,
          id: ds.id || '',
        })),
      }));

      await updateConfig.mutateAsync({
        id: currentConfig.id,
        triggers: cleanedTriggers,
      });
    } catch (error) {
      console.error('Error saving triggers:', error);
      toast.error('Failed to save triggers.');
    }
  };

  const getCategoryLabel = (categoryId: string): string => {
    const category = categories.find(
      (cat: DGPTAlertCategory) => cat.id === categoryId
    );
    if (category) {
      return `Severity ${category.severity} (${category.contactMethods.join(', ')})`;
    }
    return 'No category selected';
  };

  const getDataSourceDescription = (source: string): string => {
    const descriptions: Record<string, string> = {
      News: 'News Articles',
      Files: 'Files uploaded to Pratus',
      Web: 'Web Scraping',
      'Current Weather': 'Current weather at location',
      'Forecast Weather': 'Forecast weather at location',
      'Power Outages': 'Power outages at location',
      'ERI (Energy Reliability Index)': 'Energy Reliability Index at location',
      'ICS-Active': 'Current ICS data',
      'ICS-Historic': 'Full version history of ICS data',
      'NEO Analysis': 'Noncombatant Evacuation Order Analysis',
    };
    return descriptions[source] || source;
  };

  const tableColumns: ColumnDescription[] = [
    {
      dataField: 'name',
      text: 'Name',
      sort: true,
    },
    {
      dataField: 'categoryId',
      text: 'Category',
      sort: true,
      formatter: (cell: string) => getCategoryLabel(cell),
    },
    {
      dataField: 'data_sources',
      text: 'Data Sources',
      sort: true,
      formatter: (cell: Array<{ source: string; id: string }>) =>
        cell
          ?.map((ds: { source: string; id: string }) =>
            getDataSourceDescription(ds.source)
          )
          .join(', '),
    },
    {
      dataField: 'actions',
      text: 'Actions',
      sort: false,
      isDummyField: true,
      formatter: (_cell: any, row: DGPTAlertTrigger, index: number) => (
        <div className="d-flex gap-2">
          <StylishNewButton
            className="button--secondary button--icon"
            onClick={() => handleEditTrigger(row, index)}
          >
            <SharedIcon iconName="edit" size="sm" />
          </StylishNewButton>
          <StylishNewButton
            className="button--secondary button--icon"
            onClick={() => handleDeleteTrigger(index)}
          >
            <SharedIcon iconName="delete" size="sm" />
          </StylishNewButton>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between mb-2">
        <h6>Triggers</h6>
        <StylishNewButton
          className="button--primary"
          onClick={handleAddTrigger}
        >
          Add Trigger
        </StylishNewButton>
      </div>
      <p className="text-muted mb-4">
        Triggers define the conditions that will activate an alert. Each trigger
        monitors specific data sources for events that match your criteria, with
        severity levels aligned to the{' '}
        <Link to="/groups/alert-categories">Alert Category</Link>.
      </p>

      {triggers.length === 0 && (
        <div className="alert alert-warning mb-4">
          <strong>At least one trigger is required.</strong> Please add a
          trigger to continue to the next step.
        </div>
      )}

      <LegacyPratusTable
        keyField="name"
        rows={triggers}
        columns={tableColumns}
        noDataIndication="No triggers configured. Click 'Add Trigger' to get started."
      />

      {showDrawer && (
        <TriggerEditDrawer
          show={showDrawer}
          onHide={() => {
            setShowDrawer(false);
            setEditingTrigger(null);
            setEditingIndex(null);
          }}
          onSave={handleSaveTrigger}
          trigger={editingTrigger}
        />
      )}
    </div>
  );
};

export default TriggersStep;
